import {
  MessageBoxWidgetDto,
  ProgressBarWidgetDto,
  QuantityButtonsWidgetDto,
  WidgetType,
} from "@intelligems/ig-types";
import { Theme } from "@mui/material";
import { Dispatch } from "@reduxjs/toolkit";
import { updateUserInterface } from "@src/store/slices/userInterfaces";
import { setBatchedWidgetStyles } from "@src/store/slices/widgets";
import {
  isMessageBox,
  isProgressBar,
  isQuantityButtons,
} from "@src/store/slices/widgets/util";

import { WidgetTypographyKeys } from "../types";

import {
  createBatchedColorUpdates,
  updateAllColorsInDraft,
} from "./colorUpdates";
import { ensureTypographyField, setDraftValue } from "./draftHelpers";
import { updateDraftStyles, updateStyleOverrides } from "./styleOverrides";

interface HandlerParams {
  setStyleConfig: any;
  updateReduxStyleOverrides: (field: string, value: any) => void;
  currentUserInterface: any;
  dispatch: Dispatch<any>;
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto;
  theme: Theme;
}

export const createColorChangeHandler = ({
  setStyleConfig,
  updateReduxStyleOverrides,
  widget,
  currentUserInterface,
  dispatch,
  theme,
}: HandlerParams) => {
  return (field: string, color: string) => {
    const isPrimaryColorField = field === "theme.picker";
    const isIconActiveField = field === "breakpoint.activeIconColor";
    const isIconInactiveField = field === "breakpoint.inactiveIconColor";

    setStyleConfig((draft: any) => {
      if (isPrimaryColorField) {
        updateAllColorsInDraft(draft, color, widget, theme);
      } else if (isIconActiveField) {
        updateDraftStyles(draft, field, color);
      } else if (isIconInactiveField) {
        updateDraftStyles(draft, field, color);
      } else {
        updateDraftStyles(draft, field, color);
      }
    });

    if (isPrimaryColorField) {
      const batchedUpdates = createBatchedColorUpdates(
        currentUserInterface?.styleOverrides || {},
        color,
        widget,
        theme
      );

      if (currentUserInterface) {
        dispatch(
          updateUserInterface({
            id: currentUserInterface.id,
            styleOverrides: batchedUpdates,
          })
        );
      } else {
        dispatch(
          setBatchedWidgetStyles({
            styles: batchedUpdates,
          })
        );
      }
    } else {
      updateReduxStyleOverrides(field, color);
    }
  };
};

export const createTypographyChangeHandler = ({
  setStyleConfig,
  updateReduxStyleOverrides,
}: HandlerParams) => {
  return (
    field: WidgetTypographyKeys,
    property: "fontSize" | "fontStyle",
    value: number | string
  ) => {
    setStyleConfig((draft: any) => {
      ensureTypographyField(draft, field);
      draft.styles.typography[field][property] = value;
    });

    updateReduxStyleOverrides(`typography.${field}.${property}`, value);
  };
};

export const createStyleChangeHandler = ({
  setStyleConfig,
  updateReduxStyleOverrides,
  widget,
}: HandlerParams) => {
  return (
    property: "cornerRadius" | "spacing" | "placement",
    value: number | string
  ) => {
    if (isQuantityButtons(widget)) {
      const finalValue = property === "spacing" ? `${value}px` : value;
      setStyleConfig((draft: any) => {
        setDraftValue(draft, `styles.appearance.${property}`, finalValue);
      });
      updateReduxStyleOverrides(`appearance.${property}`, finalValue);
    } else if (isMessageBox(widget)) {
      if (property === "cornerRadius") {
        const finalValue = `${value}px`;
        setStyleConfig((draft: any) => {
          setDraftValue(draft, `styles.container.${property}`, finalValue);
        });
        updateReduxStyleOverrides(`container.${property}`, finalValue);
      } else if (property === "placement") {
        setStyleConfig((draft: any) => {
          setDraftValue(draft, `styles.${property}`, value);
        });
        updateReduxStyleOverrides(property, value);
      }
    }
  };
};

export const createProgressBarStyleChangeHandler = ({
  setStyleConfig,
  updateReduxStyleOverrides,
  widget,
}: HandlerParams) => {
  return (
    section: "bar" | "breakpoint",
    property: string,
    value: number | boolean
  ) => {
    if (isProgressBar(widget)) {
      setStyleConfig((draft: any) => {
        updateDraftStyles(draft, `${section}.${property}`, value);
      });

      updateReduxStyleOverrides(`${section}.${property}`, value);
    }
  };
};

export const createLayoutChangeHandler = ({
  setStyleConfig,
  updateReduxStyleOverrides,
  widget,
}: HandlerParams) => {
  return (layout: "row" | "column" | boolean) => {
    setStyleConfig((draft: any) => {
      if (isProgressBar(widget)) {
        setDraftValue(
          draft,
          "styles.layout.swapTextPositions",
          layout as boolean
        );
      } else {
        setDraftValue(draft, "styles.appearance.layout", layout as string);
      }
    });

    if (isProgressBar(widget)) {
      updateReduxStyleOverrides("layout.swapTextPositions", layout);
    } else {
      updateReduxStyleOverrides("appearance.layout", layout);
    }
  };
};
